import { useEffect, useState } from "react";
import EventApi from "../../Api/EventApi";
import { Link, useParams } from "react-router-dom";
import { EventParticipant } from "../../Models/EventParticipant";

type GroupedByLevel = {
  level: string;
  users: { name: string; memberName: string; id: number; memberid: number }[];
};

export default function InternalAttendeeList() {
  const { id } = useParams();
  const [grouped, setGrouped] = useState<GroupedByLevel[]>([]);
  const [eventName, setEventName] = useState<string>("");
  const [openLevels, setOpenLevels] = useState<Set<string>>(new Set());

  useEffect(() => {
    EventApi.getEventParticipants(Number(id)).then((res) => {
      const data: EventParticipant[] = res.data;

      const levelMap = new Map<
        string,
        { name: string; memberName: string; id: number; memberid: number }[]
      >();

      data.forEach((p) => {
        const key = p.memberLevel || "Unspecified";
        const fullName = `${p.firstName} ${p.lastName}`;
        if (!levelMap.has(key)) {
          levelMap.set(key, []);
        }
        levelMap.get(key)!.push({
          name: fullName,
          memberName:
            p.memberName + (p.boothNumber ? ` - Booth ${p.boothNumber}` : ""),
          id: p.userId!,
          memberid: p.memberId,
        });
      });

      const groupedArray: GroupedByLevel[] = Array.from(levelMap.entries())
        .sort((a, b) => a[0].localeCompare(b[0])) // Sort by level name
        .map(([level, users]) => ({ level, users }));

      setGrouped(groupedArray);

      // Open all by default
      const initialOpen = new Set(groupedArray.map((g) => g.level));
      setOpenLevels(initialOpen);

      if (data.length > 0 && data[0].eventName) {
        setEventName(data[0].eventName);
      }
    });
  }, [id]);

  const toggleAccordion = (level: string) => {
    setOpenLevels((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(level)) {
        newSet.delete(level);
      } else {
        newSet.add(level);
      }
      return newSet;
    });
  };

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-1">{eventName}</h1>
      <h2 className="text-xl font-semibold mb-4">
        Attendees Grouped by Member Level
      </h2>

      {/fall/i.test(eventName) && (
        <p className="text-sm text-gray-700 mb-4 rounded border border-gray-200 bg-gray-50 px-4 py-3">
          We are partnering with Farm Equipment Manufacturers Association for
          the 2026 Fall Convention.{" "}
          <a
            href="https://www.farmequip.org/meetings/fall/2026/registration/registration-list/"
            className="font-medium text-blue-600 hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Click here for their full Registration List
          </a>
          .
        </p>
      )}
      {grouped.map((group) => (
        <div key={group.level} className="mb-2 border rounded">
          <button
            className="w-full text-left px-4 py-2 bg-gray-100 hover:bg-gray-200 font-medium"
            onClick={() => toggleAccordion(group.level)}
          >
            {group.level}
          </button>
          {openLevels.has(group.level) && (
            <ul className="list-disc pl-8 pr-4 py-2 text-sm text-gray-700 bg-white">
              {group.users.map((user, idx) => (
                <li key={idx}>
                  <Link
                    to={`/user/${user.id}`}
                    className="text-blue-600 hover:underline"
                  >
                    {user.name}{" "}
                  </Link>
                  <span className="text-gray-500 text-xs">
                    <Link
                      to={`/member/${user.memberid}`}
                      className="text-blue-600 hover:underline"
                    >
                      ({user.memberName})
                    </Link>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}
